/**
 * KPI Trend Modal - Shows detailed KPI data with bar chart.
 * Refactored: API → services/kpiDetailApi.ts, hook → hooks/useKpiDetail.ts,
 * chart → components/DetailBarChart.tsx, skeleton → components/MetricsSkeleton.tsx,
 * styles → styles/kpiTrendModalStyles.ts.
 */

import React from 'react';
import {
	ActivityIndicator,
	Modal,
	Platform,
	ScrollView,
	Text,
	TouchableOpacity,
	View,
	useWindowDimensions,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { formatKpiValueByName, formatKpiTargetByName } from '../../utils/formatters';
import {
	getDeltaColor as getDeltaColorFromUnfavorable,
	getDeltaArrowIcon,
	getCategoryBackgroundColor,
	getCategoryContentColor,
	getCategoryBadgeLabel,
	resolveKpiCategory,
	KPI_CATEGORY_COLORS_DARK,
} from '../../utils/kpiHelpers';
import { useKpiDetail, KpiMetricForModal } from '../../hooks/useKpiDetail';
import { Granularity } from '../../services/kpiDetailApi';
import DetailBarChart from '../../components/DetailBarChart';
import { createKpiTrendModalStyles } from '../../styles/kpiTrendModalStyles';
import { useTheme } from '../../../../theme';

// ─── Re-export types for consumers ───────────────────────────────────────────

export type { KpiMetricForModal } from '../../hooks/useKpiDetail';

export interface KPITrendModalProps {
	visible: boolean;
	metric: KpiMetricForModal | null;
	locationToken: string;
	yearToken: string;
	periodToken: string;
	weekToken: string;
	onClose: () => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getDisplayDelta = (delta: number): number => {
	return Math.abs(delta);
};

// ─── Constants ────────────────────────────────────────────────────────────────

const GRANULARITY_OPTIONS: { label: string; value: Granularity }[] = [
	{ label: 'Daily',  value: 'daily'  },
	{ label: 'Weekly', value: 'weekly' },
	{ label: 'Period', value: 'period' },
];

// ─── Modal component ──────────────────────────────────────────────────────────

const KPITrendModal: React.FC<KPITrendModalProps> = ({
	visible,
	metric,
	locationToken,
	yearToken,
	periodToken,
	weekToken,
	onClose,
}) => {
	const { colors, resolvedTheme } = useTheme();
	const isDark = resolvedTheme === 'dark';
	const styles = createKpiTrendModalStyles(isDark, colors);
	const { width } = useWindowDimensions();
	const isMobileNative = Platform.OS !== 'web' && width < 768;
	const {
		loading,
		error,
		detail,
		granularity,
		isDropdownOpen,
		setIsDropdownOpen,
		handleGranularitySelect,
		fetchDetail,
	} = useKpiDetail({ visible, metric, locationToken, yearToken, periodToken, weekToken });

	if (!metric) { return null; }

	// Always use the card's values for Actual/Target/Delta/format/name/isUnfavorable;
	// only chart data comes from the detail API response.
	const display = metric;
	const deltaColor = getDeltaColorFromUnfavorable(display.isUnfavorable);

	// In dark mode use the deep-tinted category palette so the badge and chart
	// bars reflect the correct category accent instead of the light pastels.
	const resolvedCategory = resolveKpiCategory(display.category);
	const darkCategoryPalette = isDark && resolvedCategory ? KPI_CATEGORY_COLORS_DARK[resolvedCategory] : null;
	const badgeColor = darkCategoryPalette ? darkCategoryPalette.background : getCategoryBackgroundColor(display.category);
	const badgeTextColor = darkCategoryPalette ? darkCategoryPalette.text : getCategoryContentColor(display.category);
	const barColor = darkCategoryPalette ? darkCategoryPalette.icon : undefined;
	const badgeLabel = getCategoryBadgeLabel(display.category);
	const selectedLabel = GRANULARITY_OPTIONS.find(o => o.value === granularity)?.label ?? 'Daily';
	const hasChartData = detail && detail.chart.values.length > 0;

	const dropdownControl = (
		<View style={[styles.dropdownContainer, isMobileNative && styles.dropdownContainerMobile]}>
			<TouchableOpacity
				style={[styles.dropdownBtn, isMobileNative && styles.dropdownBtnMobile]}
				onPress={() => setIsDropdownOpen(!isDropdownOpen)}
				accessibilityRole="button"
				accessibilityLabel="Select trend granularity"
			>
				<Text style={styles.dropdownBtnText}>{selectedLabel}</Text>
				<MaterialIcons
					name={isDropdownOpen ? 'expand-less' : 'expand-more'}
					size={18}
					color={isDark ? colors.text.secondary : '#3D4147'}
				/>
			</TouchableOpacity>

			{isDropdownOpen && (
				<View style={[styles.dropdownMenu, isMobileNative && styles.dropdownMenuMobile]}>
					{GRANULARITY_OPTIONS.map(option => (
						<TouchableOpacity
							key={option.value}
							style={styles.dropdownMenuItem}
							onPress={() => handleGranularitySelect(option.value)}
							accessibilityRole="button"
							accessibilityLabel={`Show ${option.label} trend`}
						>
							<Text style={[
								styles.dropdownMenuText,
								granularity === option.value && styles.dropdownMenuTextActive,
							]}>
								{option.label}
							</Text>
						</TouchableOpacity>
					))}
				</View>
			)}
		</View>
	);

	return (
		<Modal animationType="fade" transparent visible={visible} onRequestClose={onClose}>
			<View style={styles.backdrop}>
				<View style={styles.card}>
					{/* ── Header ── */}
					{isMobileNative ? (
						// Mobile: title row (badge + title + X) on top, dropdown full-width below
						<View style={[styles.header, styles.headerMobile]}>
							<View style={[styles.headerTopRow, styles.headerTopRowMobile]}>
								<View style={[styles.headerLeft, styles.headerLeftMobile]}>
								<View style={[styles.categoryBadge, { backgroundColor: badgeColor }]}>
									<Text style={[styles.categoryBadgeText, { color: badgeTextColor }]}>{badgeLabel}</Text>
								</View>
								<View style={styles.titleRow}>
									<Text style={[styles.title, styles.titleMobile]} numberOfLines={1}>{display.name}</Text>
									{display.name.trim().toUpperCase() === 'GAP TIME' && (
										<Text style={[styles.titleSubText, styles.titleSubTextMobile]}>(Min / Week)</Text>
									)}
								</View>
								</View>
								<TouchableOpacity
									style={styles.closeBtn}
									onPress={onClose}
									accessibilityRole="button"
									accessibilityLabel="Close KPI detail modal"
								>
									<MaterialIcons name="close" size={20} color={isDark ? colors.text.secondary : '#3D4147'} />
								</TouchableOpacity>
							</View>
							<View style={styles.headerActionsMobile}>{dropdownControl}</View>
						</View>
					) : (
						// Web / tablet: single row — badge+title on left, dropdown+X on right
						<View style={styles.header}>
							<View style={styles.headerLeft}>
							<View style={[styles.categoryBadge, { backgroundColor: badgeColor }]}>
								<Text style={[styles.categoryBadgeText, { color: badgeTextColor }]}>{badgeLabel}</Text>
							</View>
							<View style={styles.titleRow}>
								<Text style={styles.title} numberOfLines={2}>{display.name}</Text>
								{display.name.trim().toUpperCase() === 'GAP TIME' && (
									<Text style={styles.titleSubText}>(Min / Week)</Text>
								)}
							</View>
							</View>
							<View style={styles.headerActions}>
								{dropdownControl}
								<TouchableOpacity
									style={styles.closeBtn}
									onPress={onClose}
									accessibilityRole="button"
									accessibilityLabel="Close KPI detail modal"
								>
									<MaterialIcons name="close" size={20} color={isDark ? colors.text.secondary : '#3D4147'} />
								</TouchableOpacity>
							</View>
						</View>
					)}

					<ScrollView
						showsVerticalScrollIndicator={false}
						contentContainerStyle={styles.scrollContent}
					>
						{/* ── Metrics ── */}
						<View style={styles.metricsContainer}>
							<View style={styles.metricCard}>
								<Text style={styles.metricLabel}>Actual</Text>
								<Text style={styles.metricValue}>
									{formatKpiValueByName(display.actual, display.format, display.name)}
								</Text>
							</View>
							<View style={styles.metricCard}>
								<Text style={styles.metricLabel2}>Target</Text>
								<Text style={styles.metricValue2}>
									{formatKpiTargetByName(display.target, display.format, display.name)}
								</Text>
							</View>
							<View style={styles.metricCard}>
								<Text style={styles.metricLabel}>Delta</Text>
								<View style={styles.deltaContainer}>
									{display.delta !== 0 && (
										<MaterialIcons
											name={getDeltaArrowIcon(display.actual, display.target)}
											size={20}
											color={deltaColor}
										/>
									)}
									<Text style={[styles.metricValue, { color: deltaColor }]}>
									{formatKpiValueByName(getDisplayDelta(display.delta), display.format, display.name)}
									</Text>
								</View>
							</View>
						</View>

						{/* ── Chart area ── */}
						<View style={styles.chartSection}>
							{loading ? (
								<View style={styles.centerState}>
									<ActivityIndicator size="small" color="#0B5FC6" />
									<Text style={styles.centerStateText}>Loading {selectedLabel.toLowerCase()} trend…</Text>
								</View>
							) : error ? (
								<View style={styles.centerState}>
									<Text style={styles.errorText}>{error}</Text>
									<TouchableOpacity
										style={styles.retryBtn}
										onPress={() => fetchDetail(granularity)}
										accessibilityRole="button"
										accessibilityLabel="Retry KPI detail API call"
									>
										<Text style={styles.retryBtnText}>Retry</Text>
									</TouchableOpacity>
								</View>
							) : !hasChartData ? (
								<View style={styles.centerState}>
									<Text style={styles.centerStateText}>No trend data available.</Text>
								</View>
							) : (
								<DetailBarChart
									labels={detail!.chart.labels}
									values={detail!.chart.values}
									format={display.format}
									metricName={display.name}
									barColor={barColor}
								/>
							)}
						</View>
					</ScrollView>
				</View>
			</View>
		</Modal>
	);
};

export default KPITrendModal;
